import { SearchIcon } from 'lucide-react';
import { PharmacySpecialtyT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { formatPhoneNumber } from '@/shared/lib/formatters';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import { Input } from '@/shared/ui/shadcn/input';

import {
  PHARMACY_SPECIALTY_LABEL_BY_VALUE,
  type PharmacySearchResultRow,
  type SavedPharmacyRow,
} from '../../model/usePharmacySelection';

interface PharmacySearchFields {
  name: string;
  city: string;
  state: string;
  zip: string;
  specialty: PharmacySpecialtyT[];
}

const SPECIALTY_OPTIONS = Object.values(PharmacySpecialtyT).filter(
  (specialty) => specialty !== PharmacySpecialtyT.Any
);

export interface PharmacySearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchFields: PharmacySearchFields;
  onSearchFieldsChange: (next: PharmacySearchFields) => void;
  onCloseResetSearchText: () => void;
  isSearchPending: boolean;
  isSearchError: boolean;
  onRetrySearch: () => void;
  savedPharmacies: SavedPharmacyRow[];
  selectedSavedPharmacyId: number | null;
  onSelectSavedPharmacy: (pharmacyId: number) => void;
  searchResults: PharmacySearchResultRow[];
  addError: string | null;
  isAddPending: boolean;
  pendingAddId: number | null;
  onAddPharmacy: (pharmacy: PharmacySearchResultRow) => void;
  triggerLabel: string;
  triggerDisabled: boolean;
}

export function PharmacySearchDialog({
  open,
  onOpenChange,
  searchFields,
  onSearchFieldsChange,
  onCloseResetSearchText,
  isSearchPending,
  isSearchError,
  onRetrySearch,
  savedPharmacies,
  selectedSavedPharmacyId,
  onSelectSavedPharmacy,
  searchResults,
  addError,
  isAddPending,
  pendingAddId,
  onAddPharmacy,
  triggerLabel,
  triggerDisabled,
}: PharmacySearchDialogProps) {
  const trimmedName = searchFields.name.trim();
  const trimmedCity = searchFields.city.trim();
  const trimmedZip = searchFields.zip.trim();
  const hasValidCriterion =
    trimmedName.length >= 3 || trimmedCity.length >= 3 || trimmedZip.length >= 3;
  // Specialty is a post-search client-side filter, not a search criterion, so
  // we deliberately exclude it from `hasAnySearchText`. If specialty toggled
  // this flag, picking a chip before typing anything would hide the saved
  // pharmacies panel (gated on `!hasAnySearchText`) and surface the
  // "Enter at least 3 characters…" empty state even though no query has run.
  const hasAnySearchText =
    trimmedName.length > 0 || trimmedCity.length > 0 || trimmedZip.length > 0;
  const searchTextError = hasValidCriterion
    ? null
    : 'Enter at least 3 characters for pharmacy name, city, or zip. Specialty filters the returned results.';
  const setSearchField = (key: Exclude<keyof PharmacySearchFields, 'specialty'>, value: string) => {
    onSearchFieldsChange({ ...searchFields, [key]: value });
  };
  const toggleSpecialty = (specialty: PharmacySpecialtyT) => {
    const nextSpecialty = searchFields.specialty.includes(specialty)
      ? searchFields.specialty.filter((value) => value !== specialty)
      : [...searchFields.specialty, specialty];
    onSearchFieldsChange({ ...searchFields, specialty: nextSpecialty });
  };

  return (
    <>
      <div className="relative">
        <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          readOnly
          value={triggerLabel}
          onClick={() => onOpenChange(true)}
          onFocus={() => onOpenChange(true)}
          className="cursor-pointer pl-9"
          disabled={triggerDisabled}
        />
      </div>

      <CommandDialog
        open={open}
        onOpenChange={(nextOpen) => {
          onOpenChange(nextOpen);
          if (!nextOpen) {
            onCloseResetSearchText();
          }
        }}
        title="Search pharmacies"
        description="Type to search pharmacies."
      >
        <CommandInput
          value={searchFields.name}
          onValueChange={(value) => setSearchField('name', value)}
          placeholder="Search pharmacies by name..."
          autoFocus
        />
        <div className="border-b px-3 py-2">
          <div className="mb-2 text-xs font-medium text-muted-foreground">Location filters</div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={searchFields.city}
              onChange={(event) => setSearchField('city', event.currentTarget.value)}
              placeholder="City"
              autoComplete="off"
              className="h-8 text-xs"
            />
            <Input
              value={searchFields.state}
              onChange={(event) => setSearchField('state', event.currentTarget.value)}
              placeholder="State"
              autoComplete="off"
              className="h-8 text-xs"
            />
            <Input
              value={searchFields.zip}
              onChange={(event) => setSearchField('zip', event.currentTarget.value)}
              placeholder="Zip"
              autoComplete="off"
              className="h-8 text-xs"
            />
          </div>
        </div>
        <div className="border-b px-3 py-2">
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="font-medium text-muted-foreground">Specialty</span>
            {searchFields.specialty.length > 0 ? (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onSearchFieldsChange({ ...searchFields, specialty: [] })}
              >
                Clear
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SPECIALTY_OPTIONS.map((specialty) => {
              const selected = searchFields.specialty.includes(specialty);
              return (
                <Button
                  key={specialty}
                  type="button"
                  variant={selected ? 'secondary' : 'outline'}
                  size="sm"
                  className={
                    selected
                      ? 'h-7 rounded-full border border-white px-2 text-[11px] ring-1 ring-white/80'
                      : 'h-7 rounded-full px-2 text-[11px]'
                  }
                  aria-pressed={selected}
                  onClick={() => toggleSpecialty(specialty)}
                >
                  {PHARMACY_SPECIALTY_LABEL_BY_VALUE[specialty] ?? specialty}
                </Button>
              );
            })}
          </div>
        </div>
        <CommandList>
          {!hasAnySearchText && savedPharmacies.length > 0 ? (
            <CommandGroup heading="Saved pharmacies">
              {savedPharmacies.map((saved) => {
                const pharmacy = saved.rxPharmacy;
                const location = [pharmacy.City, pharmacy.State, pharmacy.ZipCode]
                  .filter(
                    (value): value is string => typeof value === 'string' && value.trim().length > 0
                  )
                  .join(', ');

                return (
                  <CommandItem
                    key={String(saved.pharmacyId)}
                    value={`${saved.isPrimary ? 'Primary ' : ''}${pharmacy.StoreName ?? `Pharmacy ${saved.pharmacyId}`} ${location}`}
                    onSelect={() => onSelectSavedPharmacy(saved.pharmacyId)}
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="truncate font-medium">
                        {saved.isPrimary ? 'Primary — ' : ''}
                        {pharmacy.StoreName ?? `Pharmacy ${saved.pharmacyId}`}
                        {selectedSavedPharmacyId === saved.pharmacyId ? ' (selected)' : ''}
                      </div>
                      {location.length > 0 ? (
                        <div className="text-muted-foreground truncate text-xs">{location}</div>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ) : null}

          {!hasAnySearchText ? (
            <CommandEmpty>Enter search criteria to find pharmacies.</CommandEmpty>
          ) : !hasValidCriterion ? (
            <CommandEmpty>{searchTextError}</CommandEmpty>
          ) : isSearchPending ? (
            <CommandEmpty>Searching…</CommandEmpty>
          ) : isSearchError ? (
            <CommandEmpty>
              Search failed.
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void onRetrySearch()}
                >
                  Retry
                </Button>
              </div>
            </CommandEmpty>
          ) : searchResults.length === 0 ? (
            <CommandEmpty>No pharmacies found.</CommandEmpty>
          ) : (
            <CommandGroup heading="Results">
              {searchResults.map((result) => {
                const pharmacy = result.fallbackRxPharmacy;
                const location = [pharmacy.City, pharmacy.State, pharmacy.ZipCode]
                  .filter(
                    (value): value is string => typeof value === 'string' && value.trim().length > 0
                  )
                  .join(', ');
                const specialties =
                  Array.isArray(pharmacy.PharmacySpecialties) &&
                  pharmacy.PharmacySpecialties.length > 0
                    ? pharmacy.PharmacySpecialties
                    : [];
                const isPendingForRow = pendingAddId === result.pharmacyId;
                const isDisabled = isAddPending;

                return (
                  <CommandItem
                    key={String(result.pharmacyId)}
                    value={`${result.label} ${location}`}
                    disabled={isDisabled}
                    onSelect={() => void onAddPharmacy(result)}
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="truncate font-medium">
                        {pharmacy.StoreName ?? result.label}
                        {isPendingForRow ? ' (adding...)' : ''}
                      </div>
                      {pharmacy.Address1 ? (
                        <div className="text-muted-foreground truncate text-xs">
                          {pharmacy.Address1}
                          {pharmacy.Address2 ? `, ${pharmacy.Address2}` : ''}
                        </div>
                      ) : null}
                      {location.length > 0 ? (
                        <div className="text-muted-foreground truncate text-xs">{location}</div>
                      ) : null}
                      {pharmacy.PrimaryPhone ? (
                        <div className="text-muted-foreground truncate text-xs">
                          {formatPhoneNumber(pharmacy.PrimaryPhone)}
                        </div>
                      ) : null}
                      {specialties.length > 0 ? (
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {specialties.map((specialty) => (
                            <Badge key={specialty} variant="secondary" className="text-[10px]">
                              {specialty}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
        {addError ? (
          <div className="text-destructive border-t px-3 py-2 text-sm">{addError}</div>
        ) : null}
      </CommandDialog>
    </>
  );
}
